import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Check, Copy, X } from 'lucide-react';
import { analyticsApi, type AnalyticsApi } from '../../api/analytics';
import type { AnalyticsSession, AnalyticsTimelineItem } from '../../api/types';
import { Badge } from '../../components/Badge';
import { ErrorNote } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import { useLoad } from '../../lib/useLoad';
import { useTimeFormat } from '../../lib/tenantTime';
import { channelLabel, duration, flagOf, money, percent } from './analyticsFormat';

/**
 * One visit, opened out.
 *
 * This is the replacement for DOM session replay: every pageview and every
 * event the visit produced, interleaved into one ordered rail with dwell,
 * engaged time, scroll depth and what it ended in. It answers the question an
 * operator actually asks — "what did this person look at before they called" —
 * without recording a single keystroke or pixel of the visitor's screen.
 *
 * Everything on the rail is text the tenant's own site supplied (paths, titles,
 * event names, event props). It is rendered as text and never as markup.
 */
export function SessionDrawer({
  sessionKey,
  siteId,
  onClose,
  api = analyticsApi,
}: {
  sessionKey: string;
  siteId?: number;
  onClose: () => void;
  api?: AnalyticsApi;
}) {
  const { fmtDateTime } = useTimeFormat();
  const load = useLoad(() => api.getAnalyticsSession(sessionKey, siteId), [sessionKey, siteId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const session = load.data?.session ?? null;
  const items = load.data?.payload ?? [];
  const visitor = load.data?.visitor ?? null;

  // Offsets are measured from the session's own start, so the gap between two
  // steps is readable down the rail. The absolute clock time lives once in the
  // header — repeating it on every row would spend the column on the part
  // nobody compares.
  const startMs = useMemo(() => (session ? new Date(session.startedAt).getTime() : 0), [session]);

  return (
    <div
      className="an-drawer-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside className="an-drawer" role="dialog" aria-modal="true" aria-label="Visit detail">
        <div className="an-drawer-head">
          <div>
            <div className="an-title">{session ? titleOf(session) : 'Visit'}</div>
            <div className="an-sub">
              {session ? fmtDateTime(session.startedAt) : sessionKey}
              {session?.endedAt ? ` · ${duration(elapsed(session))} on site` : null}
            </div>
          </div>
          <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="Close">
            <X size={15} />
          </button>
        </div>

        {load.error ? <ErrorNote error={load.error} /> : null}
        {load.loading && !session ? (
          <div className="an-empty">
            <Spinner />
          </div>
        ) : null}

        {session ? (
          <>
            <div className="an-badges">
              {session.outcome === 'booking' ? (
                <Badge tone="ok">
                  Booked{session.bookingValue ? ` · ${money(session.bookingValue)}` : ''}
                </Badge>
              ) : session.outcome === 'lead' ? (
                <Badge tone="warn">Left a quote</Badge>
              ) : null}
              {session.isIdentified ? <Badge tone="accent">Signed in</Badge> : <Badge>Anonymous</Badge>}
              {session.isBounce ? <Badge>Single page</Badge> : null}
              <Badge>{channelLabel(session.channel)}</Badge>
            </div>

            <dl className="an-facts">
              <Fact label="Who">
                {session.contactName ??
                  (session.isIdentified ? 'Signed in, no contact matched' : 'Not signed in')}
              </Fact>
              <Fact label="Where">{placeOf(session)}</Fact>
              <Fact label="On">{deviceOf(session)}</Fact>
              <Fact label="Came from">{sourceOf(session)}</Fact>
              <Fact label="Engaged">
                {duration(session.engagedSeconds)} over {session.pageviews}{' '}
                {session.pageviews === 1 ? 'page' : 'pages'}
              </Fact>
              <Fact label="Entered on">{session.entryPath ?? '—'}</Fact>
              <Fact label="Left from">{session.exitPath ?? '—'}</Fact>
            </dl>

            {items.length ? (
              <ol className="an-timeline">
                {items.map((item, index) => (
                  <Step key={stepKey(item, index)} item={item} offsetSeconds={offsetOf(item, startMs)} />
                ))}
              </ol>
            ) : (
              <div className="an-empty">
                Nothing on the rail. The raw detail of a visit is kept for 90 days — a visit older than
                that keeps its totals in the reports but loses its individual steps.
              </div>
            )}

            {visitor ? (
              <div className="an-caveat">
                This visitor has been seen {visitor.sessionCount}{' '}
                {visitor.sessionCount === 1 ? 'time' : 'times'} since {fmtDateTime(visitor.firstSeenAt)}
                {visitor.firstChannel ? `, first arriving from ${channelLabel(visitor.firstChannel)}` : ''}.
                {/* What an erasure request is answered with (Install and
                    settings → Data & removal), which used to tell the operator
                    to copy it from here while nothing here showed it. */}
                <span className="an-visitor-id">
                  Visitor id <code>{visitor.key}</code>
                  <CopyId value={visitor.key} />
                </span>
              </div>
            ) : null}
            <div className="an-caveat">
              Engaged time counts only the seconds the tab was open in front of someone and something was
              happening — it is deliberately smaller than the wall-clock time a page was left open, which is
              shown beside it as dwell. A last page with no dwell is one whose tab was closed without the
              browser giving us a final beacon.
            </div>
          </>
        ) : null}
      </aside>
    </div>
  );
}

function CopyId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      className="btn btn-ghost btn-icon"
      aria-label={copied ? 'Visitor id copied' : 'Copy the visitor id'}
      title={copied ? 'Copied' : 'Copy'}
      onClick={() =>
        // Without clipboard access (an insecure origin, where `clipboard` is
        // not even defined, or a frame the host did not allow it) the id is
        // still on screen, and one click on it selects the whole of it.
        void Promise.resolve()
          .then(() => navigator.clipboard.writeText(value))
          .then(
            () => setCopied(true),
            () => setCopied(false),
          )
      }
    >
      {copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="an-fact">
      <dt className="an-fact-label">{label}</dt>
      <dd className="an-fact-value">{children}</dd>
    </div>
  );
}

function Step({ item, offsetSeconds }: { item: AnalyticsTimelineItem; offsetSeconds: number }) {
  return (
    <li className="an-step">
      <span className="an-step-time">+{offsetLabel(offsetSeconds)}</span>
      <span className="an-step-rail">
        <span className={item.kind === 'event' ? 'an-step-dot event' : 'an-step-dot'} />
      </span>
      <span>
        {item.kind === 'pageview' ? <PageviewStep item={item} /> : <EventStep item={item} />}
      </span>
    </li>
  );
}

function PageviewStep({ item }: { item: Extract<AnalyticsTimelineItem, { kind: 'pageview' }> }) {
  const parts: string[] = [];
  parts.push(item.dwellSeconds === null ? 'dwell not reported' : `${duration(item.dwellSeconds)} on page`);
  parts.push(`${duration(item.engagedSeconds)} engaged`);
  if (item.clickCount) parts.push(`${item.clickCount} ${item.clickCount === 1 ? 'click' : 'clicks'}`);
  const vitals = vitalsOf(item.webVitals);
  return (
    <>
      <span className="an-step-path">{item.path}</span>
      {item.title ? <span className="an-step-meta">{item.title}</span> : null}
      <span className="an-step-meta">{parts.join(' · ')}</span>
      <span
        className="an-scrollbar"
        title={`Scrolled ${percent(item.maxScrollPct / 100, 0)} of the page`}
        aria-label={`Scrolled ${percent(item.maxScrollPct / 100, 0)} of the page`}
      >
        <span style={{ width: `${Math.max(2, Math.min(100, item.maxScrollPct))}%` }} />
      </span>
      {vitals ? <span className="an-step-meta">{vitals}</span> : null}
    </>
  );
}

function EventStep({ item }: { item: Extract<AnalyticsTimelineItem, { kind: 'event' }> }) {
  const props = propsLabel(item.props);
  return (
    <>
      <span className="an-step-path">{item.name}</span>
      {props ? <span className="an-step-meta an-props">{props}</span> : null}
    </>
  );
}

// ---- pure helpers ---------------------------------------------------------

const titleOf = (session: AnalyticsSession): string =>
  session.contactName || (session.entryPath ?? 'Visit');

const elapsed = (session: AnalyticsSession): number => {
  if (!session.endedAt) return session.engagedSeconds;
  const span = (new Date(session.endedAt).getTime() - new Date(session.startedAt).getTime()) / 1000;
  return Number.isFinite(span) && span > 0 ? span : session.engagedSeconds;
};

const offsetOf = (item: AnalyticsTimelineItem, startMs: number): number => {
  const at = new Date(item.at).getTime();
  if (!Number.isFinite(at) || !startMs) return 0;
  return Math.max(0, (at - startMs) / 1000);
};

/** `0:00`, `4:07`, `1:12:40` — a stopwatch, so gaps read down the column. */
const offsetLabel = (seconds: number): string => {
  const total = Math.round(seconds);
  const s = String(total % 60).padStart(2, '0');
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}:${s}`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}:${s}`;
};

const placeOf = (session: AnalyticsSession): string => {
  const parts = [session.city, session.region, session.country].filter(Boolean);
  if (!parts.length) return 'Unknown';
  const flag = flagOf(session.country);
  return `${flag ? `${flag} ` : ''}${parts.join(', ')}`;
};

const deviceOf = (session: AnalyticsSession): string => {
  if (session.platform === 'app') {
    return [session.os, session.appVersion ? `app ${session.appVersion}` : null, session.device]
      .filter(Boolean)
      .join(' · ');
  }
  return [session.browser, session.os, session.device].filter(Boolean).join(' · ') || 'Unknown';
};

const sourceOf = (session: AnalyticsSession): string => {
  const utm = [session.utm.source, session.utm.medium, session.utm.campaign].filter(Boolean);
  if (utm.length) return `${channelLabel(session.channel)} · ${utm.join(' / ')}`;
  if (session.referrerHost) return `${channelLabel(session.channel)} · ${session.referrerHost}`;
  return channelLabel(session.channel);
};

/**
 * Web vitals are only shown when the browser actually reported them — Safari
 * supplies no INP and no LCP, so a zero here would be a claim about the page
 * rather than a measurement of it.
 */
const vitalsOf = (vitals: { lcpMs: number | null; cls: number | null; inpMs: number | null }): string => {
  const parts: string[] = [];
  if (vitals.lcpMs !== null) parts.push(`LCP ${(vitals.lcpMs / 1000).toFixed(1)}s`);
  if (vitals.inpMs !== null) parts.push(`INP ${Math.round(vitals.inpMs)}ms`);
  if (vitals.cls !== null) parts.push(`CLS ${vitals.cls.toFixed(2)}`);
  return parts.join(' · ');
};

/** Event props, flattened and clipped. Tenant free text — text, never markup. */
const propsLabel = (props: Record<string, unknown> | null): string => {
  if (!props || typeof props !== 'object') return '';
  const parts: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || typeof value === 'object') continue;
    parts.push(`${key}: ${String(value).slice(0, 60)}`);
    if (parts.length >= 5) break;
  }
  return parts.join(' · ');
};

/**
 * Pageviews carry a server-assigned `seq`; events do not, and two events can
 * share a millisecond. The index is what keeps the key unique in that case.
 */
const stepKey = (item: AnalyticsTimelineItem, index: number): string =>
  item.kind === 'pageview' ? `pv-${item.seq}` : `ev-${index}-${item.at}`;
